/* eslint-disable react/jsx-max-props-per-line, react/jsx-one-expression-per-line */
import React, { useEffect, useState } from "react";
import { TableCell, TableRow } from "src/components/ui/table";
import { useGetInvoiceLineItemValidationsQuery } from "src/redux/billing/billing.endpoints";
import { Invoice, InvoiceLineItem, SKU, ValidationMismatchEntry } from "src/redux/billing/billing.types";
import {
  AttentionReason,
  computeDerivedLineItemRateAndMargin,
  fieldValue,
  formatCurrency,
  getAttentionReasons,
  getAttentionReasonsFromGroupMismatches,
  getLineItemFieldValidationOutcome,
} from "../../utils/billing-utils";
import { formatNumber, formatPercentage } from "../../utils/utils";
import BorderedInput from "src/components/form-new/bordered-input";
import { Company } from "../../types/billing.types";
import BorderedSelect from "src/components/form-new/bordered-select";
import InvoiceDrawerLineTableRowMenu from "./invoice-drawer-line-table-row-menu";
import { AlertTriangle, NotebookPen, X } from "lucide-react";
import StatusBadgeThin from "./status-badge-thin";
import { Button } from "src/components/ui/button";


interface InvoiceDrawerLineTableRowProps {
  lineItem: InvoiceLineItem;
  invoice: Invoice;
  skus: Array<SKU>;
  invoices: Array<Invoice>;
  companies: Array<Company>;
  isLocked: boolean;
  updateLineItem: (lineItemId: string, field: string, value: any) => Promise<void>;
  /** From invoice_group.validations.mismatches; when set, used instead of line item details for mismatch UI */
  validationMismatches?: ValidationMismatchEntry[];
  refetchInvoiceGroup: () => void | Promise<void>;
}

const InvoiceDrawerLineTableRow: React.FC<InvoiceDrawerLineTableRowProps> = ({
  lineItem,
  invoice,
  skus,
  invoices,
  companies,
  isLocked,
  updateLineItem,
  validationMismatches,
  refetchInvoiceGroup,
}) => {
  const { data: lineItemValidations = [] } = useGetInvoiceLineItemValidationsQuery(
    { line_item: lineItem.id },
    { skip: !lineItem.id }
  );

  const [notes, setNotes] = useState(lineItem.notes);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingField, setIsLoadingField] = useState<string | null>(null);

  const formatCurrencyFn = (n: number) => formatCurrency(Number(n), "USD", 2, 6);
  const formatPercentageFn = (n: number) => formatPercentage(Number(n));

  const hasGroupMismatches = (validationMismatches?.length ?? 0) > 0;
  let attentionReasons: AttentionReason[] = [];
  if (hasGroupMismatches) {
    attentionReasons = getAttentionReasonsFromGroupMismatches(
      validationMismatches!,
      lineItem.id,
      lineItem,
      formatCurrencyFn,
      formatPercentageFn,
      formatNumber
    );
  } else {
    attentionReasons = getAttentionReasons(lineItem, formatCurrencyFn, formatPercentageFn, formatNumber);
  }

  const isPipelineLine =
    lineItem.billing_import != null || Boolean(lineItem.details?.pipeline_primitive);

  const showAttentionReasons = lineItem.attention_needed && attentionReasons.length > 0;
  const [applyingField, setApplyingField] = useState<string | null>(null);

  const handleApplyExpected = async(fieldKey: string, expectedValue: number) => {
    if (isLocked) return;
    setApplyingField(fieldKey);
    await updateLineItem(lineItem.id, fieldKey, expectedValue);
    setApplyingField(null);
  };

  const handleApplyAllExpected = async() => {
    if (isLocked) return;
    setApplyingField('all');
    for (const r of attentionReasons) {
      await updateLineItem(lineItem.id, r.fieldKey, r.expectedValue);
    }
    setApplyingField(null);
  };

  useEffect(() => {
    setNotes(lineItem.notes);
  }, [lineItem.notes]);

  const { rate: derivedRate, marginDollars: derivedMarginDollars, amountToBill: derivedAmountToBill } =
    computeDerivedLineItemRateAndMargin(lineItem);

  const handleUpdateLineItem = async(field: string, value: any) => {
    if (lineItem[field as keyof InvoiceLineItem]?.toString().trim().toLowerCase() !== value.toString().trim().toLowerCase()) {
      setIsLoading(true);
      setIsLoadingField(field);
      await updateLineItem(lineItem.id, field, value);
      setIsLoading(false);
      setIsLoadingField(null);
    }
  };

  return (
    <>
      <TableRow
        key={lineItem.id}
        className={`${notes ? '' : 'border-b border-gray-200'}`}
      >
        <TableCell
          className="text-gray-600 text-sm text-left align-middle py-3"
        >
          <div className="flex flex-col items-start justify-center gap-1">
            <div className="flex max-w-full flex-row flex-wrap items-center justify-start gap-0.5">
              <StatusBadgeThin
                status={lineItem.invoice_line_item_status || 'draft'}
                attention_needed={lineItem.attention_needed}
                fieldValidationOutcome={getLineItemFieldValidationOutcome(lineItem)}
                lineItemValidations={lineItemValidations}
                lineItemId={lineItem.id}
                invoiceGroupId={lineItem.invoice_group}
                isLocked={isLocked}
                updateLineItem={updateLineItem}
                refetchInvoiceGroup={refetchInvoiceGroup}
              />
            </div>
            {isPipelineLine && (
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                Pipeline
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="py-3 text-gray-600 text-sm align-middle">
          <BorderedSelect
            searchEnabled={true}
            disabled={isLocked || (isLoading && isLoadingField === 'sku')}
            options={skus?.map((sku) => ({ value: sku.id, label: sku.name ?? '' }))}
            value={fieldValue(lineItem, 'sku')}
            onChange={(value) => handleUpdateLineItem('sku', value.target.value)}
          />
        </TableCell>
        <TableCell className="py-3 text-gray-600 text-sm align-middle">
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'name')}
            className={`${isLoading && isLoadingField === 'name' ? 'animate-pulse' : ''}`}
            useTooltip={true}
            label={ lineItem.platform && lineItem.channel ? lineItem.platform.toLowerCase().replace('_', ' ') + '  (' + lineItem.channel.toLowerCase().replace('_', ' ') + ')' : ''}
            value={lineItem.name || ''}
            onBlur={(value) => handleUpdateLineItem('name', value.target.value)}
          />
        </TableCell>
        <TableCell className="py-3 text-gray-600 text-sm align-middle">
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'qty')}
            className={`${isLoading && isLoadingField === 'qty' ? 'animate-pulse' : ''}`}
            value={fieldValue(lineItem, 'qty')}
            formatDisplay={(value) => formatNumber(Number(value))}
            onBlur={(value) => handleUpdateLineItem('qty', value.target.value)}
          />
        </TableCell>
        <TableCell className={`py-3 text-sm cursor-not-allowed align-middle text-gray-600 ${isLocked ? 'opacity-50' : 'text-gray-600'}`}>
          {formatCurrency(derivedRate, 'USD', 2, 6)}
        </TableCell>
        <TableCell className="py-3 text-gray-600 text-sm align-middle">
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'margin_pct')}
            className={`${isLoading && isLoadingField === 'margin_pct' ? 'animate-pulse' : ''}`}
            label={formatCurrency(derivedMarginDollars, 'USD', 0, 0)}
            value={fieldValue(lineItem, 'margin_pct')}
            formatDisplay={(value) => formatPercentage(Number(value))}
            onBlur={(value) => handleUpdateLineItem('margin_pct', value.target.value)}
            onKeyDown={(value: React.KeyboardEvent<HTMLInputElement>) => {
              if (value.key === 'Enter') {
                updateLineItem(lineItem.id, 'margin_pct', value.currentTarget.value);
              }
            }}
          />
        </TableCell>
        <TableCell className="py-3 text-gray-600 text-sm align-middle">
          <BorderedInput
            disabled={isLocked || (isLoading && isLoadingField === 'advertiser_spend')}
            className={`${isLoading && isLoadingField === 'advertiser_spend' ? 'animate-pulse' : ''}`}
            label={`${formatCurrency(derivedAmountToBill, 'USD', 0, 0)}`}
            value={fieldValue(lineItem, 'advertiser_spend')}
            formatDisplay={(value) => formatCurrency(Number(value))}
            onBlur={(value) => handleUpdateLineItem('advertiser_spend', value.target.value)}
            onKeyDown={(value: React.KeyboardEvent<HTMLInputElement>) => {
              if (value.key === 'Enter') {
                updateLineItem(lineItem.id, 'advertiser_spend_override', value.currentTarget.value);
              }
            }}
          />
        </TableCell>

        <TableCell className="py-3 text-gray-600 text-sm align-middle">
          <div className="flex items-center justify-end">
            <InvoiceDrawerLineTableRowMenu
              lineItem={lineItem}
              invoice={invoice}
              skus={skus}
              invoices={invoices}
              companies={companies}
              isLocked={isLocked}
              setNotes={(notes) => {setNotes(notes); }}
              updateLineItem={updateLineItem}
            />
          </div>
        </TableCell>
      </TableRow>
      {showAttentionReasons && (
        <TableRow className="border-b border-gray-200">
          <TableCell className="text-gray-600 text-sm" />
          <TableCell colSpan={8} className="p-0">
            <div className="text-amber-900 bg-warning-100 p-2 rounded-sm text-sm flex items-start gap-2 w-full">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-2 w-full">
                <span className="font-medium">Attention needed — validation mismatch:</span>
                <ul className="list-disc list-inside space-y-1">
                  {attentionReasons.map((r) => (
                    <li key={r.label} className="flex flex-wrap items-center gap-2">
                      <span>{r.label}: expected {r.expected}, got {r.actual}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isLocked || applyingField !== null}
                        onClick={() => handleApplyExpected(r.fieldKey, r.expectedValue)}
                        className="h-7 text-xs shrink-0"
                      >
                        {applyingField === r.fieldKey ? 'Applying…' : 'Apply'}
                      </Button>
                    </li>
                  ))}
                </ul>
                <div className="pt-1 border-t border-warning-200/80">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isLocked || applyingField !== null}
                    onClick={handleApplyAllExpected}
                    className="h-7 text-xs bg-warning-50 hover:bg-warning-200 border-warning-400"
                  >
                    {applyingField === 'all' ? 'Applying…' : 'Apply all'}
                  </Button>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
      {notes && (
        <TableRow className="border-b border-gray-200">
          <TableCell className="text-gray-600 text-sm" />
          <TableCell
            colSpan={8}
            className="p-0"
          >
            <div className="text-gray-600 bg-gray-200 p-2 rounded-sm text-sm italic flex items-center gap-2 w-full justify-between">
              <div className="flex items-center gap-2 w-full">
                <NotebookPen className='h-4 w-4' />
                <input
                  disabled={isLocked || (isLoading && isLoadingField === 'notes')}
                  type="text"
                  value={notes || ''}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={(e) => handleUpdateLineItem('notes', e.target.value)}
                  className={`text-gray-600 bg-gray-200 text-sm italic flex items-center gap-2 w-full focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent border-b border-gray-201 ${isLoading && isLoadingField === 'notes' ? 'animate-pulse' : ''}`}
                />
              </div>
              <Button
                disabled={isLocked || (isLoading && isLoadingField === 'notes')}
                variant="ghost"
                size="sm"
                onClick={() => {setNotes(null); updateLineItem(lineItem.id, 'notes', null);}}
                className="text-gray-600 hover:text-gray-800 justify-end text-right"
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default InvoiceDrawerLineTableRow;